/**
 * Customer behavior analytics API service
 * 
 * Provides methods for accessing customer behavior tracking data,
 * including product views, catalogue engagement, trending products,
 * and customer journey analytics.
 */
import apiClient from './api';
import {
  TrendingProduct,
  ProductInterestMetrics,
  SeenNotSeenStats,
  OpenRateMetric,
  PaginatedProductViewsResponse,
  PaginatedCatalogueViewsResponse,
  CustomerJourneyResponse,
  BehaviorFilters
} from '../types/customerBehavior';

export class BehaviorService {
  /**
   * Get product view analytics with optional filters
   * 
   * @param filters - Filter parameters including customer_id, period, pagination
   * @returns Paginated list of product views with customer and product details
   */
  static async getProductViews(filters?: BehaviorFilters): Promise<PaginatedProductViewsResponse> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/product-views', {
        params: {
          customer_id: filters?.customer_id,
          period: filters?.period || '7d',
          limit: filters?.limit || 100,
          offset: filters?.offset || 0,
          category: filters?.category,
          start_date: filters?.start_date,
          end_date: filters?.end_date
        }
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching product views:', error);
      throw error;
    }
  }

  /**
   * Get catalogue view analytics with aggregation metrics
   * 
   * @param filters - Filter parameters including customer_id, period, pagination
   * @returns Paginated list of catalogue views with engagement metrics
   */
  static async getCatalogueViews(filters?: BehaviorFilters): Promise<PaginatedCatalogueViewsResponse> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/catalogue-views', {
        params: {
          customer_id: filters?.customer_id,
          period: filters?.period || '7d',
          limit: filters?.limit || 100,
          offset: filters?.offset || 0,
          start_date: filters?.start_date,
          end_date: filters?.end_date
        }
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching catalogue views:', error);
      throw error;
    }
  }

  /**
   * Get customer journey timeline for a specific customer
   * 
   * @param customerId - Customer ID to retrieve journey for
   * @param filters - Filter parameters including period and activity types
   * @returns Customer journey with chronological event timeline
   */
  static async getCustomerJourney(
    customerId: string,
    filters?: BehaviorFilters
  ): Promise<CustomerJourneyResponse> {
    try {
      const response = await apiClient.get(
        `/admin/analytics/behavior/customer-journey/${customerId}`,
        {
          params: {
            period: filters?.period || '30d',
            activity_types: filters?.activity_types?.join(','),
            start_date: filters?.start_date,
            end_date: filters?.end_date
          }
        }
      );
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching customer journey:', error);
      throw error;
    }
  }

  /**
   * Get trending products ordered by view count
   * 
   * @param filters - Filter parameters including period and limit
   * @returns List of trending products with conversion rates
   */
  static async getTrendingProducts(filters?: BehaviorFilters): Promise<TrendingProduct[]> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/trending-products', {
        params: {
          period: filters?.period || '7d',
          limit: filters?.limit || 20,
          start_date: filters?.start_date,
          end_date: filters?.end_date
        }
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching trending products:', error);
      throw error;
    }
  }

  /**
   * Get product interest metrics grouped by interest level
   * 
   * @param filters - Filter parameters including customer_type and period
   * @returns Product interest metrics categorized as high/medium/low
   */
  static async getProductInterest(filters?: BehaviorFilters): Promise<ProductInterestMetrics> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/product-interest', {
        params: {
          customer_type: filters?.customer_type,
          period: filters?.period || '30d',
          start_date: filters?.start_date,
          end_date: filters?.end_date
        }
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching product interest:', error);
      throw error;
    }
  }

  /**
   * Get seen vs not seen product statistics
   * 
   * @param filters - Filter parameters including customer_id
   * @returns Statistics on product visibility and coverage
   */
  static async getSeenNotSeen(filters?: BehaviorFilters): Promise<SeenNotSeenStats> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/seen-not-seen', {
        params: {
          customer_id: filters?.customer_id
        }
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching seen/not seen stats:', error);
      throw error;
    }
  }

  /**
   * Get open rate metrics for catalogues or products
   * 
   * @param filters - Filter parameters including entity_type, period, customer_type
   * @returns Open rate metrics with period-over-period comparisons
   */
  static async getOpenRates(filters?: BehaviorFilters): Promise<OpenRateMetric[]> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/open-rates', {
        params: {
          entity_type: filters?.entity_type || 'catalogue',
          period: filters?.period || '30d',
          customer_type: filters?.customer_type,
          start_date: filters?.start_date,
          end_date: filters?.end_date
        }
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error fetching open rates:', error);
      throw error;
    }
  }

  /**
   * Export product views to CSV
   * 
   * @param filters - Filter parameters to apply to export
   * @returns Blob containing CSV data
   */
  static async exportProductViews(filters?: BehaviorFilters): Promise<Blob> {
    try {
      const response = await apiClient.get('/admin/analytics/behavior/product-views/export', {
        params: {
          customer_id: filters?.customer_id,
          period: filters?.period || '7d',
          category: filters?.category,
          start_date: filters?.start_date,
          end_date: filters?.end_date
        },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      console.error('[BehaviorService] Error exporting product views:', error);
      throw error;
    }
  }
}
